import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Box, Container } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import WasherListResults from 'src/components/washers/WasherListResults';
import WasherListToolbar from 'src/components/washers/WasherListToolbar';
import { getWasherList, removeWasher, updateWasherByID } from 'src/redux/washers/action';
import { api } from 'src/service';
import { usePersistedPagerInfo } from 'src/utils/usePersistedLimit';

const WasherList = () => {
  const dispatch = useDispatch();
  const [washers, setWashers] = useState([]);
  const [newWashers, setNewWashers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [washerID, setWasherID] = useState();
  const [searchStr, setSearchStr] = useState('');
  const [pagerInfo, setPagerInfo] = usePersistedPagerInfo('washers', { limit: 10 });
  // Default sort: status asc puts 'active' before 'inactive'.
  const [sortBy, setSortBy] = useState('washer.status');
  const [sortOrder, setSortOrder] = useState('asc');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSearch = () => {
    setSearchStr('new');
  };

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        await api
          .post('/backoffice/washers/filter', {
            page_size: pagerInfo.limit,
            current_page: pagerInfo.page,
            search: searchStr,
            sort_by: sortBy,
            sort_order: sortOrder,
          })
          .then((res) => {
            const payload = (res && res.data && res.data.data) || {};
            setWashers(Array.isArray(payload.data) ? payload.data : []);
            setPagerInfo({
              limit: payload.page_size || pagerInfo.limit,
              page: payload.current_page || pagerInfo.page,
              totalSize: payload.total_size || 0,
            });
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[washers] fetch failed', error);
        setWashers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchStr, pagerInfo.page, pagerInfo.limit, sortBy, sortOrder]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        await api.get('/washer/status/new').then((res) => {
          setNewWashers(res.data.data.length);
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[washers/new] fetch failed', error);
        setNewWashers(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleRow = async (id) => {
    handleOpen(true);
    setWasherID(id);
  };

  const handleRemove = async () => {
    setOpen(false);
    setLoading(true);
    await dispatch(removeWasher(washerID));
    const data = await dispatch(getWasherList());
    setWashers(data);
    setLoading(false);
  };

  const handleToggleActive = async (id, currentStatus) => {
    const newStatus = currentStatus === 'inactive' ? 'active' : 'inactive';
    const ok = window.confirm(`Set washer to ${newStatus.toUpperCase()}?`);
    if (!ok) return;
    setLoading(true);
    try {
      await dispatch(updateWasherByID({ status: newStatus }, id));
      const data = await dispatch(getWasherList());
      setWashers(data);
    } catch (e) {
      window.alert('Failed to update status: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    try {
      const res = await api.post('/backoffice/washers/filter', {
        page_size: pagerInfo.limit,
        current_page: pagerInfo.page,
        search: searchStr,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const payload = (res && res.data && res.data.data) || {};
      setWashers(Array.isArray(payload.data) ? payload.data : []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[washers] refetch failed', e);
    }
  };

  const handleToggleWorkMode = async (id, currentMode) => {
    const newMode = currentMode === 'online' ? 'offline' : 'online';
    const ok = window.confirm(`Set washer to ${newMode.toUpperCase()}?`);
    if (!ok) return;
    setLoading(true);
    try {
      await api.patch(`/backoffice/workmode/${id}`, { work_mode: newMode });
      await refetch();
    } catch (e) {
      window.alert('Failed to update work mode: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchStr = (str) => {
    setSearchStr(str);
  };

  const handlePagerInfo = (info) => {
    setPagerInfo(info);
  };

  const handleSortChange = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setPagerInfo((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <Helmet>
        <title>Washer - Washers</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3,
        }}
      >
        <Container maxWidth={false}>
          <WasherListToolbar
            handleSearchStr={handleSearchStr}
            newWashers={newWashers}
            handleSearch={handleSearch}
          />

          <Box sx={{ pt: 3 }}>
            <WasherListResults
              washers={washers}
              handleRow={handleRow}
              handleToggleActive={handleToggleActive}
              handleToggleWorkMode={handleToggleWorkMode}
              loading={loading}
              pagerInfo={pagerInfo}
              handlePagerInfo={handlePagerInfo}
              onSortChange={handleSortChange}
            />
          </Box>

          <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogTitle id="responsive-dialog-title">Confirmation</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to remove it?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={handleClose} color="primary">
                Disagree
              </Button>
              <Button onClick={handleRemove} color="primary" autoFocus>
                Agree
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </>
  );
};

export default WasherList;
